import { Fragment, useState } from "react";
import { Listbox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/24/outline";

interface TextInputMultiLanguageProps {
  id: string;
  type: string;
  label?: string;
  labelColour?: string;
  className: string;
  topClassName?: string;
  ariaLabel?: string;
  placeholder?: string;
  autoComplete?: string;
  required?: boolean;
  children?: React.ReactNode;
  showLabel?: boolean;
  state: any;
  stateName: any;
  onChange?: any;
  valueHandler?: any;
  valueHandlerArgs?: any[];
  onChangeHandler?: any;
  onChangeHandlerArgs?: any[];
  disabled?: boolean;
  small?: string | null;
  defaultLanguage: string;
  languageChoices?: string[];
  rows?: number;
}

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

export default function TextInputMultiLanguage({
  id,
  type,
  label,
  labelColour,
  className,
  topClassName,
  ariaLabel,
  placeholder,
  autoComplete,
  required,
  children,
  showLabel,
  state,
  stateName,
  valueHandler,
  valueHandlerArgs,
  onChange,
  onChangeHandler,
  onChangeHandlerArgs,
  disabled,
  small,
  defaultLanguage,
  languageChoices,
  rows,
}: TextInputMultiLanguageProps) {
  // Set component level state
  const [selectedLanguage, setSelectedLanguage] =
    useState<string>(defaultLanguage);

  // Define variables
  const tabs =
    languageChoices === undefined
      ? [
          {
            id: "EN",
            icon: <span className="fi fi-gb"></span>,
          },
          {
            id: "FR",
            icon: <span className="fi fi-fr"></span>,
          },
          {
            id: "IT",
            icon: <span className="fi fi-it"></span>,
          },
          {
            id: "DE",
            icon: <span className="fi fi-de"></span>,
          },
        ]
      : languageChoices.map((language) => {
          if (language === "EN")
            return {
              id: language,
              icon: <span className={`fi fi-gb`}></span>,
            };
          return {
            id: language,
            icon: <span className={`fi fi-${language.toLowerCase()}`}></span>,
          };
        });

  return (
    <div className={topClassName}>
      {/* Label */}
      {showLabel ? (
        <label
          htmlFor={id}
          className={`mb-3 block text-left text-sm font-medium ${labelColour}`}
        >
          {label}
        </label>
      ) : (
        <label
          htmlFor={id}
          className={`block text-left text-sm font-medium ${labelColour}`}
        />
      )}

      <div className="flex row">
        {/* Language dropdown */}
        <Listbox value={selectedLanguage} onChange={setSelectedLanguage}>
          {({ open }) => (
            <>
              {/* <Listbox.Label className="text-sm font-medium leading-6 text-gray-900">Assigned to</Listbox.Label> */}
              <div className="relative flex-none">
                {/* Visible value for dropdown */}
                <Listbox.Button className="h-full relative cursor-default rounded-md rounded-r-none bg-white py-1.5 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-primary sm:text-sm sm:leading-6">
                  <span className="flex items-center">
                    {tabs.find((tab) => tab.id === selectedLanguage)?.icon}
                  </span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 ml-3 flex items-center pr-2">
                    <ChevronUpDownIcon
                      className="h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                  </span>
                </Listbox.Button>

                {/* Dropdown */}
                <Transition
                  show={open}
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <Listbox.Options className="absolute z-10 mt-1 max-h-56 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                    {tabs.map((tab) => (
                      <Listbox.Option
                        key={tab.id}
                        className={({ active }) =>
                          classNames(
                            active ? "bg-primary text-white" : "text-gray-900",
                            "relative cursor-default select-none py-2 pl-3 pr-9",
                          )
                        }
                        value={tab.id}
                      >
                        {({ selected, active }) => (
                          <>
                            <div className="flex items-center">{tab.icon}</div>
                            {selected ? (
                              <span
                                className={classNames(
                                  active ? "text-white" : "text-primary",
                                  "absolute inset-y-0 right-0 flex items-center px-2",
                                )}
                              >
                                <CheckIcon
                                  className="h-5 w-5"
                                  aria-hidden="true"
                                />
                              </span>
                            ) : null}
                          </>
                        )}
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </Transition>
              </div>
            </>
          )}
        </Listbox>

        {/* Text input */}
        {type === "text" && (
          <>
            {disabled ? (
              <input
                id={id}
                type={type}
                className={className}
                placeholder={placeholder}
                aria-label={ariaLabel}
                autoComplete={autoComplete}
                required={required}
                onChange={(e) =>
                  onChange !== null
                    ? onChange({
                        ...state,
                        [stateName]: {
                          ...state[stateName],
                          [selectedLanguage]: e.target.value,
                        },
                      })
                    : onChangeHandler(e, onChangeHandlerArgs, selectedLanguage)
                }
                value={
                  valueHandler === null
                    ? state[stateName][selectedLanguage]
                    : valueHandler(valueHandlerArgs, selectedLanguage)
                }
                disabled
              >
                {children}
              </input>
            ) : (
              <input
                id={id}
                type={type}
                className={className}
                placeholder={placeholder}
                aria-label={ariaLabel}
                autoComplete={autoComplete}
                value={
                  valueHandler === null
                    ? state[stateName][selectedLanguage]
                    : valueHandler(valueHandlerArgs, selectedLanguage)
                }
                required={required}
                onChange={(e) =>
                  onChange !== null
                    ? onChange({
                        ...state,
                        [stateName]: {
                          ...state[stateName],
                          [selectedLanguage]: e.target.value,
                        },
                      })
                    : onChangeHandler(e, onChangeHandlerArgs, selectedLanguage)
                }
              >
                {children}
              </input>
            )}
          </>
        )}

        {/* Text area */}
        {type === "textarea" && (
          <>
            {disabled ? (
              <textarea
                id={id}
                className={className}
                placeholder={placeholder}
                aria-label={ariaLabel}
                autoComplete={autoComplete}
                required={required}
                onChange={(e) =>
                  onChange !== null
                    ? onChange({
                        ...state,
                        [stateName]: {
                          ...state[stateName],
                          [selectedLanguage]: e.target.value,
                        },
                      })
                    : onChangeHandler(e, onChangeHandlerArgs, selectedLanguage)
                }
                value={
                  valueHandler === null
                    ? state[stateName][selectedLanguage]
                    : valueHandler(valueHandlerArgs, selectedLanguage)
                }
                rows={rows}
                disabled
              >
                {children}
              </textarea>
            ) : (
              <textarea
                id={id}
                rows={rows}
                className={className}
                placeholder={placeholder}
                aria-label={ariaLabel}
                autoComplete={autoComplete}
                value={
                  valueHandler === null
                    ? state[stateName][selectedLanguage]
                    : valueHandler(valueHandlerArgs, selectedLanguage)
                }
                required={required}
                onChange={(e) =>
                  onChange !== null
                    ? onChange({
                        ...state,
                        [stateName]: {
                          ...state[stateName],
                          [selectedLanguage]: e.target.value,
                        },
                      })
                    : onChangeHandler(e, onChangeHandlerArgs, selectedLanguage)
                }
              >
                {children}
              </textarea>
            )}
          </>
        )}
      </div>

      {small !== null && (
        <p className="text-xs text-left text-secondary/70 mt-2">{small}</p>
      )}
    </div>
  );
}

TextInputMultiLanguage.defaultProps = {
  ariaLabel: "",
  labelColour: "text-black",
  topClassName: "",
  placeholder: "",
  autoComplete: "",
  required: true,
  showLabel: false,
  valueHandler: null,
  valueHandlerArgs: [],
  onChange: null,
  onChangeHandler: null,
  onChangeHandlerArgs: [],
  disabled: false,
  small: null,
  defaultLanguage: "EN",
  languageChoices: ["EN", "FR", "IT", "DE"],
  rows: 4,
};
